<script lang="ts">
    import {onMount} from "svelte";
    import {get, retire} from "$lib/api";
    import CardLayout from "$items/CardLayout.svelte";
    import {Badge, Button, Search, TabItem, Tabs, Tooltip} from "flowbite-svelte";
    import SortableTable from "$lib/components/SortableTable.svelte";
    import ImageRenderer from "$lib/components/ui/ImageRenderer.svelte";
    import TagsRenderer from "$lib/components/ui/TagsRenderer.svelte";
    import DocumentActions from "$lib/partials/documents/DocumentActions.svelte";
    import {storageDocuments} from "$lib/stores/documents";
    import RemoveButton from "$lib/components/ui/RemoveButton.svelte";
    import {
        FolderDuplicateSolid,
        GridOutline, ListOutline,
    } from "flowbite-svelte-icons";
    import {listView} from "$lib/stores/viewStore";
    import {globalConfirm} from "$lib/stores/modal";
    import DocumentVersionRenderer from "$lib/components/ui/DocumentVersionRenderer.svelte";

    let documents = storageDocuments;

    let searchFilter = "";

    $: tagsFilter = (tags: string[]) => {
        let filtered = [...$documents];
        for(let i = 0; i < tags.length; i++) {
            filtered = filtered.filter(x => x.tags?.some(y => y.toLowerCase() === tags[i].toLowerCase()));
        }
        return filtered;
    }
    const accordions = [
        {
            title: 'Board',
            categories: [
                {name: 'Geschäftsführung', filter: ['Board', 'Geschäftsführung']},
                {name: 'General', filter:['Board', 'General']},
            ]
        },{
            title: 'Dispo',
            categories: [
                {name: 'USM', filter:['Dispo', 'USM']},
                {name: 'USB', filter:['Dispo', 'USB'] },
                {name: 'USH', filter:['Dispo', 'USH']},
                {name: 'UKM', filter:['Dispo', 'UKM']},
                {name: 'UKB', filter:['Dispo', 'UKB']},
                {name: 'UKH', filter:['Dispo', 'UKH']},
            ]
        },{
            title: 'AUNA',
            categories: [
                {name: 'USM', filter:['AUNA', 'USM']},
                {name: 'USB', filter:['AUNA', 'USB'] },
                {name: 'USH', filter:['AUNA', 'USH']},
                {name: 'UKM', filter:['AUNA', 'UKM']},
                {name: 'UKB', filter:['AUNA', 'UKB']},
                {name: 'UKH', filter:['AUNA', 'UKH']},
            ]
        },{
            title: 'Sales',
            categories: [
                {name: 'USM', filter:['Sales', 'USM']},
                {name: 'USB', filter:['Sales', 'USB'] },
                {name: 'USH', filter:['Sales', 'USH']},
                {name: 'UKM', filter:['Sales', 'UKM']},
                {name: 'UKB', filter:['Sales', 'UKB']},
                {name: 'UKH', filter:['Sales', 'UKH']},
            ]
        },{
            title: 'FiBu',
            categories: [
                {name: 'Ausgang', filter:['AUNA', 'Ausgang']},
                {name: 'Eingang', filter:['AUNA', 'Eingang']},
            ]
        },{
            title: 'Work',
            categories: [
                {name: 'Recruiting', filter:['Work', 'Recruiting']},
                {name: 'Get-to-know', filter:['Work', 'Get-to-know'] },
                {name: 'Street Aktion', filter:['Work', 'Street Aktion'] },
                {name: 'Sonstiges', filter:['Work', 'Sonstiges'] },
            ]
        },{
            title: 'Rebuddy',
            categories: [
                {name: 'Rebuddy', filter:['Rebuddy']},
            ]
        },{
            title: 'Legal',
            categories: [
                {name: 'AÜV', filter:['Legal', 'AÜV']},
                {name: 'MAV', filter:['Legal', 'MAV']},
                {name: 'Gesetzestexte | Literatur | Referentenentwürfe', filter:['Legal', 'Gesetzestexte | Literatur | Referentenentwürfe']},
                {name: 'ABGs', filter:['Legal', 'AGB']},
            ]
        },{
            title: 'Remedia',
            categories: [
                {name: 'Remedia', filter:['Remedia']},
            ]
        },
    ]
    const header = [
        {
            property: 'name',
            label: 'Name',
            sortable: true,
            movable: true,
            class: 'w-1/2',
        }, {
            property: 'location',
            label: 'Document',
            sortable: true,
            movable: true,
            component: ImageRenderer,

        }, {
            property: 'index',
            label: 'Version',
            sortable: true,
            movable: true,
            component: DocumentVersionRenderer,
            customSort:(a,b,c) => a.createdAt.stamp > b.createdAt.stamp ? c : -1*c
        }, {
            property: 'tags',
            label: 'Tag(s)',
            movable: true,
            component: TagsRenderer,
        }, {
            property: 'uuid',
            label: 'Entfernen',
            movable: true,
            component: RemoveButton,
            props: {
                onClick: (uuid: string) => {
                    $globalConfirm.confirm('Wirklich löschen?', async () => {
                        await retire('/document/' + uuid)
                        $documents = $documents.filter(x => x.uuid !== uuid)
                    })
                }
            }
        }
    ]

    $: filtered = searchFilter === '' ? $documents : filterDocuments()

    const filterDocuments = () => {

        let consideredDocuments = [...$documents];

        const searchTypes = searchFilter.split(':').map(x => x.trim());
        if(searchTypes.length > 1) {
            consideredDocuments = tagsFilter(searchTypes[1].split(' ').map(x => x.trim()).filter(x => x !== ''));
        }

        return consideredDocuments.filter(x => x.name.toLowerCase().includes(searchTypes[0].toLowerCase()));
    }

    onMount(async () => {
        $documents = await get('/storage-documents')
    })
</script>
<svelte:head>
    <title>Documents</title>
</svelte:head>
<div class="flex">
    <div class="flex-1">
        {#if $listView}
            <div class="border-b p-1 pb-2">
                <Search bind:value={searchFilter} placeholder="Namensfilter : tag1 tag2" />
            </div>

            <SortableTable data={filtered} {header}>
                <svelte:fragment slot="empty">
                    <div class="px-3 py-2 text-neutral-500 flex gap-2">Leer</div>
                </svelte:fragment>
            </SortableTable>
        {:else }
            <Tabs contentClass=" " inactiveClasses="p-4 text-gray-500 rounded-t-lg hover:text-gray-600 border-b-2 border-transparent hover:border-primary-400" activeClasses="p-4 text-primary-600 rounded-t-lg border-b-2 border-primary-600" tabStyle="underline">
                {#each accordions.sort((a, b) => a.title.localeCompare(b.title)) as accordion, i}
                    <TabItem open={i ===0} >
                        <div class="relative flex" slot="title" >
                            <FolderDuplicateSolid />
                            <span class="px-4">{accordion.title}</span>
<!--                            <Badge class="text-xs font-bold">{tagsFilter([accordion.title]).length}</Badge>-->
                        </div>
                        <div class="mt-5 space-y-3">
                            {#each accordion.categories as category}
                                <CardLayout title={category.name} accordion display={accordion.categories.length===1}>
                                    <div class="relative mt-1" slot="card-header">
                                        <Badge color="blue" class="text-xs font-bold">{tagsFilter(category.filter).length}</Badge>
                                    </div>
                                    <div class="px-4 py-3 mb-5 space-y-2">
                                        <div class="flex gap-3">
                                            <div class="flex-1">
                                                <SortableTable data={tagsFilter(category.filter)} {header}>
                                                    <svelte:fragment slot="empty">
                                                        <div class="px-3 py-2 text-neutral-500 flex gap-2">Leer</div>
                                                    </svelte:fragment>
                                                </SortableTable>
                                            </div>
                                            <div class="w-44 border-l border-neutral-200 pl-3">
                                                <DocumentActions category={accordion} />
                                            </div>
                                        </div>
                                    </div>
                                </CardLayout>
                            {/each}
                        </div>


                    </TabItem>
                {/each}
            </Tabs>
        {/if}

    </div>

    <div class="pr-3 pt-1 border-b pb-3 place-self-start">
        <Button on:click={() => $listView = !$listView} color="alternative" pill class="!p-2" >
            {#if $listView}
                <ListOutline class="text-primary-600" />
            {:else}
                <GridOutline class="text-primary-600" />
            {/if}
        </Button>
        <Tooltip placement="left">Ansicht wechseln</Tooltip>
    </div>
</div>


